import { useCallback } from 'react';
import { analyticsDeactivateRider, analyticsReactivateRider } from '@rideiq/api/services/Analytics';
import {
  deactivateRider,
  editUpdatePauseRider,
  pauseRider,
  reactivateRider,
  unpauseRider,
  updatePartialRiderWithPhoto,
  updatePauseRider,
  updateUnpauseRider,
} from '@rideiq/api/services/Riders';
import { useToastStore } from '@rideiq/shared/stores/toastStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RIDE_SCHEDULE_QUERY_KEY, RIDER_QUERY_KEY } from './useRiderQuery';

const PAUSE_TOAST_MESSAGE =
  'Rides for this rider have been paused. It may take several minutes to be updated in your ride list. Please refresh your browser. SMS notifications will be sent to CareGivers and Riders, too, if they have a phone number.';

export const useRiderMutations = (slug: string | null) => {
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();

  const invalidateRider = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [RIDER_QUERY_KEY, slug] });
  }, [queryClient, slug]);

  const reactivateMutation = useMutation({
    mutationFn: () => reactivateRider(slug!),
  });

  const handleReactivateRider = useCallback(async () => {
    if (!slug) {
      return;
    }
    const response = await reactivateMutation.mutateAsync();

    if (response.type === 'success') {
      invalidateRider();
      analyticsReactivateRider(slug);
    } else {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }
  }, [slug, reactivateMutation, invalidateRider, addToast]);

  const deactivateMutation = useMutation({
    mutationFn: (data: { reasonKey?: string } = {}) => deactivateRider(slug!, data),
  });

  const handleDeactivateRider = useCallback(
    async (data: { reasonKey?: string } = {}) => {
      if (!slug) {
        return;
      }
      const response = await deactivateMutation.mutateAsync(data);

      if (response.type === 'success') {
        invalidateRider();
        queryClient.invalidateQueries({ queryKey: [RIDE_SCHEDULE_QUERY_KEY, slug] });
        analyticsDeactivateRider(slug);
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }
    },
    [slug, deactivateMutation, invalidateRider, queryClient, addToast]
  );

  const pauseMutation = useMutation({
    mutationFn: (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      const abortController = new AbortController();

      return pauseRider(slug!, data, abortController.signal);
    },
  });

  const handlePauseRider = useCallback(
    async (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      if (!slug) {
        return null;
      }
      const response = await pauseMutation.mutateAsync(data);

      if (response.type !== 'error') {
        invalidateRider();
        addToast({ severity: 'info', message: PAUSE_TOAST_MESSAGE });
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }

      return response;
    },
    [slug, pauseMutation, invalidateRider, addToast]
  );

  const unpauseMutation = useMutation({
    mutationFn: (data: { startDate: string }) => {
      const abortController = new AbortController();

      return unpauseRider(slug!, data, abortController.signal);
    },
  });

  const handleUnpauseRider = useCallback(
    async (data: { startDate: string }) => {
      if (!slug) {
        return null;
      }
      const response = await unpauseMutation.mutateAsync(data);

      if (response.type !== 'error') {
        invalidateRider();
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }

      return response;
    },
    [slug, unpauseMutation, invalidateRider, addToast]
  );

  const updatePauseMutation = useMutation({
    mutationFn: (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      const abortController = new AbortController();

      return updatePauseRider(slug!, data, abortController.signal);
    },
  });

  const handleUpdatePauseRider = useCallback(
    async (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      if (!slug) {
        return null;
      }
      const response = await updatePauseMutation.mutateAsync(data);

      if (response.type !== 'error') {
        invalidateRider();
        addToast({ severity: 'info', message: PAUSE_TOAST_MESSAGE });
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }

      return response;
    },
    [slug, updatePauseMutation, invalidateRider, addToast]
  );

  const editUpdatePauseMutation = useMutation({
    mutationFn: (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      const abortController = new AbortController();

      return editUpdatePauseRider(slug!, data, abortController.signal);
    },
  });

  const handleEditUpdatePauseRider = useCallback(
    async (data: { startDate: Nullable<string>; endDate: Nullable<string> }) => {
      if (!slug) {
        return null;
      }
      const response = await editUpdatePauseMutation.mutateAsync(data);

      if (response.type !== 'error') {
        invalidateRider();
        addToast({ severity: 'info', message: PAUSE_TOAST_MESSAGE });
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }

      return response;
    },
    [slug, editUpdatePauseMutation, invalidateRider, addToast]
  );

  const updateUnpauseMutation = useMutation({
    mutationFn: () => {
      const abortController = new AbortController();

      return updateUnpauseRider(slug!, abortController.signal);
    },
  });

  const handleUpdateUnpauseRider = useCallback(async () => {
    if (!slug) {
      return null;
    }
    const response = await updateUnpauseMutation.mutateAsync();

    if (response.type !== 'error') {
      invalidateRider();
      addToast({ severity: 'info', message: 'Pause ended successfully' });
    } else {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }

    return response;
  }, [slug, updateUnpauseMutation, invalidateRider, addToast]);

  const saveProfileMutation = useMutation({
    mutationFn: (formRiderDetails: Partial<RiderDetails>) =>
      updatePartialRiderWithPhoto(slug!, formRiderDetails as unknown as RiderFormType),
  });

  const handleSaveProfile = useCallback(
    async (formRiderDetails: Partial<RiderDetails>) => {
      if (!slug) {
        return null;
      }
      const response = await saveProfileMutation.mutateAsync(formRiderDetails);

      if (response.type !== 'error') {
        invalidateRider();
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }

      return response;
    },
    [slug, saveProfileMutation, invalidateRider, addToast]
  );

  return {
    reactivateRider: handleReactivateRider,
    deactivateRider: handleDeactivateRider,
    pauseRider: handlePauseRider,
    unpauseRider: handleUnpauseRider,
    updatePauseRider: handleUpdatePauseRider,
    editUpdatePauseRider: handleEditUpdatePauseRider,
    updateUnpauseRider: handleUpdateUnpauseRider,
    saveProfile: handleSaveProfile,
  };
};
